import { createGeotab, Credentials, Geotab, User } from "@fairfleet/geotab";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import LocalStorageNames from "@/constants/LocalStorageNames";
import { getUsers } from "@/hooks/users/useUsersBySearch";

// Interface for the context type
interface GeotabClientContextType {
  geotabClient: Geotab;
  updateGeotabClient: (newCredentials: Credentials) => void;
  switchSession: (sessionId: string) => void;
  clearGeotabClient: (sessionId: string) => void;
  username: string;
  database: string;
  geotabView: boolean;
  activeSessionId: string | null;
  internalSessionId: string | null;
  sessions: string[]; // List of available sessions
  isLoading: boolean; // Is the client being
  me: User | null;
}

// Default geotabClient initialization (null while loading)
const defaultGeotabClient = createGeotab();

export const GeotabClientContext = createContext<GeotabClientContextType>({
  geotabClient: defaultGeotabClient,
  updateGeotabClient: () => ({}),
  switchSession: () => ({}),
  clearGeotabClient: () => ({}),
  username: "",
  database: "",
  geotabView: false,
  activeSessionId: null,
  internalSessionId: null,
  sessions: [],
  isLoading: true, // Start with loading
  me: null,
});

export const useGeotabClient = () => {
  return useContext(GeotabClientContext);
};

// Utility function to get all available sessions from localStorage
const getStoredSessions = () => {
  const keys = Object.keys(localStorage);
  return keys
    .filter(key => key.startsWith(LocalStorageNames.sessionKeyPrefix))
    .map(key => key.replace(LocalStorageNames.sessionKeyPrefix, ""));
};

// Provider component with session handling
export const GeotabClientProvider: React.FC<{ children: ReactNode; credentials?: Credentials }> = ({
  children,
  credentials,
}) => {
  const [geotabClient, setGeotabClient] = useState<Geotab>(createGeotab({ credentials }));
  const [username, setUsername] = useState<string>(credentials?.userName ?? "");
  const [database, setDatabase] = useState<string>(credentials?.database ?? "");
  const [sessions, setSessions] = useState<string[]>(getStoredSessions);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [internalSessionId, setInternalSessionId] = useState<string | null>(
    credentials?.sessionId ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [me, setMe] = useState<User | null>(null);

  useEffect(() => {
    async function getMe() {
      const users = await getUsers(geotabClient, username, false, false);
      setMe(users?.[0] ?? null);
    }
    if (!isLoading) {
      getMe();
    }
  }, [isLoading, geotabClient, username]);

  useEffect(() => {
    const savedSessionId = localStorage.getItem(LocalStorageNames.activeSessionKey);
    if (savedSessionId) {
      setActiveSessionId(savedSessionId);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Load credentials from localStorage if activeSessionId is set
  useEffect(() => {
    if (activeSessionId) {
      const savedCredentials = localStorage.getItem(
        `${LocalStorageNames.sessionKeyPrefix}${activeSessionId}`,
      );
      if (savedCredentials) {
        const parsedCredentials: Credentials = JSON.parse(savedCredentials);
        setGeotabClient(createGeotab({ credentials: parsedCredentials }));
        setUsername(parsedCredentials.userName);
        setDatabase(parsedCredentials.database);
        setInternalSessionId(parsedCredentials.sessionId);
        setIsLoading(false); // Client loaded, stop loading
      }
    }
  }, [activeSessionId]);

  // Function to update the Geotab client and store credentials in localStorage
  const updateGeotabClient = useCallback((newCredentials: Credentials) => {
    const sessionId = newCredentials.userName + " (" + newCredentials.database + ")";
    setGeotabClient(createGeotab({ credentials: newCredentials }));
    setUsername(newCredentials.userName);
    setInternalSessionId(newCredentials.sessionId);
    setDatabase(newCredentials.database);
    localStorage.setItem(
      `${LocalStorageNames.sessionKeyPrefix}${sessionId}`,
      JSON.stringify(newCredentials),
    );
    localStorage.setItem(LocalStorageNames.activeSessionKey, sessionId); // Persist the active session ID
    setActiveSessionId(sessionId); // Automatically switch to the new session
    setSessions(getStoredSessions());
    setIsLoading(false); // Set loading to false once the client is set
  }, []);

  // Function to switch between sessions
  const switchSession = useCallback(
    (sessionId: string) => {
      const savedCredentials = localStorage.getItem(
        `${LocalStorageNames.sessionKeyPrefix}${sessionId}`,
      );
      if (savedCredentials) {
        const parsedCredentials: Credentials = JSON.parse(savedCredentials);
        setGeotabClient(createGeotab({ credentials: parsedCredentials }));
        setUsername(parsedCredentials.userName);
        setInternalSessionId(parsedCredentials.sessionId);
        setDatabase(parsedCredentials.database);
        localStorage.setItem(LocalStorageNames.activeSessionKey, sessionId); // Persist the active session ID
        setActiveSessionId(sessionId);
      } else {
        setGeotabClient(createGeotab({ credentials })); // Set to null while switching session
        setUsername("");
        setDatabase("");
        setInternalSessionId(null);
        localStorage.removeItem(LocalStorageNames.activeSessionKey); // Clear active session
        setActiveSessionId(null);
      }
      setIsLoading(false); // Always set loading to false once done switching
    },
    [credentials],
  );

  // Function to clear the Geotab client and remove credentials for a specific session
  const clearGeotabClient = useCallback(
    (sessionId: string) => {
      localStorage.removeItem(`${LocalStorageNames.sessionKeyPrefix}${sessionId}`);
      if (activeSessionId === sessionId) {
        localStorage.removeItem(LocalStorageNames.activeSessionKey);
        setActiveSessionId(null);
      }
      const newSessions = getStoredSessions();
      setSessions(newSessions);
      if (newSessions.length === 0) {
        setGeotabClient(createGeotab());
        setUsername("");
        setInternalSessionId(null);
        setDatabase("");
      } else {
        switchSession(newSessions[0]);
      }
      setIsLoading(false);
    },
    [activeSessionId, switchSession],
  );

  return (
    <GeotabClientContext.Provider
      value={{
        geotabClient,
        updateGeotabClient,
        switchSession,
        clearGeotabClient,
        username,
        database,
        geotabView: !!credentials,
        activeSessionId,
        sessions,
        isLoading,
        me,
        internalSessionId,
      }}
    >
      {children}
    </GeotabClientContext.Provider>
  );
};
